import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: Record<string, any>) => string;
  dir: 'ltr' | 'rtl';
}

const translations = {
  en: {
    // Navigation
    shippingManagementSystem: 'Shipping Management System',
    globalSourcingShipping: 'Global Sourcing & Shipping Solutions',
    dashboard: 'Dashboard',
    customers: 'Customers',
    warehouses: 'Warehouses',
    shipments: 'Shipments',
    trips: 'Trips',
    invoices: 'Invoices',
    users: 'Users',
    success: 'Success',
    error: 'Error',

    // Common Actions
    home: 'Home',
    search: 'Search',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    actions: 'Actions',
    view: 'View',
    close: 'Close',
    submit: 'Submit',
    login: 'Login',
    logout: 'Logout',
    register: 'Register',

    // Additional UI elements
    all: 'All',
    filter: 'Filter',
    filters: 'Filters',
    history: 'History',
    print: 'Print',
    download: 'Download',
    markAsSent: 'Mark as Sent',
    markAsPaid: 'Mark as Paid',
    profile: 'Profile',
    settings: 'Settings',
    create: 'Create',
    update: 'Update',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',

    // Wallet
    wallet: 'Wallet',
    walletManagement: 'Wallet Management',

    // Login
    username: 'Username',
    usernameOrEmail: 'Username or Email',
    enterUsernameOrEmail: 'Enter username or email',
    password: 'Password',
    enterUsername: 'Enter username',
    enterPassword: 'Enter password',
    loginSuccessful: 'Login Successful',
    welcomeBack: 'Welcome back!',
    loginFailed: 'Login Failed',
    invalidCredentials: 'Invalid username or password',
    defaultCredentials: 'Default Credentials',
    admin: 'Admin',
    warehouseUser: 'Warehouse User',

    // Dashboard
    totalCustomers: 'Total Customers',
    totalWarehouses: 'Total Warehouses',
    activeShipments: 'Active Shipments',
    pendingInvoices: 'Pending Invoices',
    recentActivity: 'Recent Activity',
    quickStats: 'Quick Statistics',
    totalTrips: 'Total Trips',
    active: 'Active',
    inactive: 'Inactive',
    yourWarehouse: 'Your warehouse',
    maintenance: 'Maintenance',

    // Customers
    addCustomer: 'Add Customer',
    customerName: 'Customer Name',
    customerCode: 'Customer Code:',
    manageCustomerAccounts: 'Manage your customer accounts with unique 4-digit codes',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    registrationDate: 'Registration Date',
    noCustomers: 'No customers found',

    // Warehouses
    addWarehouse: 'Add Warehouse',
    warehouseName: 'Warehouse Name',
    warehouseType: 'Warehouse Type',
    location: 'Location',
    capacity: 'Capacity',
    currentStock: 'Current Stock',
    main: 'Main',
    branch: 'Branch',
    distribution: 'Distribution',
    noWarehouses: 'No warehouses found',
    ourWarehouseLocations: 'Our Warehouse Locations',
    contactInfoForWarehouses: 'Contact information for our warehouses',
    mainWarehouse: 'Main Warehouse',
    warehouseManager: 'Warehouse Manager',
    notAssigned: 'Not assigned',
    noWarehouseInfoAvailable: 'No warehouse information available',

    // Shipments
    addShipment: 'Add Shipment',
    createShipment: 'Create Shipment',
    trackingNumber: 'Tracking Number',
    customer: 'Customer',
    trackAndManageShipments: 'Track and manage all shipments with real-time status updates',
    origin: 'Origin',
    destination: 'Destination',
    weight: 'Weight',
    volume: 'Volume',
    packages: 'Packages',
    status: 'Status',
    pending: 'Pending',
    inTransit: 'In Transit',
    delivered: 'Delivered',
    noShipments: 'No shipments found',
    allShipments: 'All Shipments',
    processing: 'Processing',
    atDestination: 'At Destination',

    // Trips
    addTrip: 'Add Trip',
    tripNumber: 'Trip Number',
    driver: 'Driver',
    vehicle: 'Vehicle',
    departure: 'Departure',
    arrival: 'Arrival',
    totalWeight: 'Total Weight',
    totalVolume: 'Total Volume',
    totalPackages: 'Total Packages',
    noTrips: 'No trips found',
    allTrips: 'All Trips',
    planning: 'Planning',
    completed: 'Completed',

    // Invoices
    addInvoice: 'Add Invoice',
    invoiceNumber: 'Invoice Number',
    date: 'Date',
    dueDate: 'Due Date',
    subtotal: 'Subtotal',
    tax: 'Tax',
    total: 'Total',
    paymentStatus: 'Payment Status',
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    noInvoices: 'No invoices found',
    allInvoices: 'All Invoices',
    partial: 'Partial',
    viewDraftInvoices: 'View all invoices that are still in draft status',
    viewSentInvoices: 'View all invoices that have been sent to customers',
    viewPaidInvoices: 'View all invoices that have been fully paid',
    viewOverdueInvoices: 'View all invoices that are past their due date',
    viewPartialInvoices: 'View all invoices that have been partially paid',
    generateCombinedInvoices: 'Generate combined invoices for multiple shipments',

    // Customer Dashboard
    welcomeCustomer: 'Welcome {customerName}!',
    customerCode: 'Customer Code: ',
    totalShipments: 'Total Shipments',
    inTransit: 'In Transit',
    totalInvoices: 'Total Invoices',
    outstanding: 'Outstanding',
    shipmentStatus: 'Shipment Status',
    currentStatusOfShipments: 'Current status of all your shipments',
    processing: 'Processing',
    atDestination: 'At Destination',
    delivered: 'Delivered',
    recentShipments: 'Recent Shipments',
    viewAll: 'View All',
    latestShipmentActivity: 'Your latest shipment activity',
    noShipmentsYet: 'No shipments yet',
    recentInvoices: 'Recent Invoices',
    latestInvoiceActivity: 'Your latest invoice activity',
    noInvoicesYet: 'No invoices yet',
    amount: 'Amount',
    quickActions: 'Quick Actions',
    commonTasksAndShortcuts: 'Common tasks and shortcuts',
    viewMyShipments: 'View My Shipments',
    viewMyInvoices: 'View My Invoices',
    myProfile: 'My Profile',
    notAvailable: 'N/A',

    // Customer Profile
    myProfile: 'My Profile',
    adminAccountInformation: 'Admin Account Information',
    warehouseManagerAccountInformation: 'Warehouse Manager Account Information',
    customerAccountInformation: 'Customer Account Information',
    administratorAccountDetails: 'Administrator account details',
    warehouseManagerAccountDetails: 'Warehouse manager account details',
    customerAccountDetails: 'Customer account details',
    accountInformation: 'Account Information',
    username: 'Username',
    editProfile: 'Edit Profile',
    changePassword: 'Change Password',
    personalInformation: 'Personal Information',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    state: 'State',
    country: 'Country',
    destinationWarehouse: 'Destination Warehouse',
    warehouseInformation: 'Warehouse Information',
    warehouseName: 'Warehouse Name',
    warehouseCode: 'Warehouse Code',
    warehouseType: 'Warehouse Type',
    warehouseLocation: 'Warehouse Location',
    warehouseCapacity: 'Warehouse Capacity',
    warehouseCurrentStock: 'Current Stock',
    warehouseStatus: 'Warehouse Status',
    security: 'Security',
    changeYourPassword: 'Change Your Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordChangedSuccessfully: 'Password changed successfully',
    profileUpdatedSuccessfully: 'Profile updated successfully',
    saveChanges: 'Save Changes',
    cancel: 'Cancel',
    accountType: 'Account Type',
    customerCode: 'Customer Code',
    status: 'Status',
    warehouseCode: 'Warehouse Code',
    warehouseName: 'Warehouse Name',
    active: 'Active',

    // Dashboard
    totalRevenue: 'Total Revenue',
    revenueByWarehouse: 'Revenue by Warehouse',
    shipmentsByStatus: 'Shipments by Status',
    tripsByStatus: 'Trips by Status',
    invoicesByStatus: 'Invoices by Status',
    warehousesByType: 'Warehouses by Type',
    warehousesByStatus: 'Warehouses by Status',
    customersByStatus: 'Customers by Status',
    recentShipments: 'Recent Shipments',
    latestShipmentActivities: 'Latest shipment activities and status updates',
    processing: 'Processing',
    inTransit: 'In Transit',
    atDestination: 'At Destination',
    delivered: 'Delivered',
    planning: 'Planning',
    completed: 'Completed',
    main: 'Main',
    branch: 'Branch',
    distribution: 'Distribution',
    active: 'Active',
    inactive: 'Inactive',
    maintenance: 'Maintenance',
    totalShipments: 'Total Shipments',
    activeTrips: 'Active Trips',
    pendingInvoices: 'Pending Invoices',
    totalCustomers: 'Total Customers',
    totalWarehouses: 'Total Warehouses',
    totalTrips: 'Total Trips',
    quickStats: 'Quick Statistics',
    revenue: 'Revenue',
    percentage: 'Percentage',
    customerInformation: 'Customer Information',
    generatedOn: 'Generated on',
    thankYou: 'Thank you for your business!',
    billTo: 'Bill To',
    invoiceDetails: 'Invoice Details',
    issueDate: 'Issue Date',
    paidDate: 'Paid Date',
    pricingModel: 'Pricing Model',
    shipmentDetails: 'Shipment Details',
    no: 'No',
    description: 'Description',
    packages: 'Packages',
    pricePerKg: 'Price (per kg)',
    pricePerPiece: 'Price (per piece)',
    pricePerShipment: 'Price (per shipment)',
    amount: 'Amount',
    additionalItems: 'Additional Items',
    discount: 'Discount',
    grandTotal: 'TOTAL',
    amountPaid: 'Amount Paid',
    balanceDue: 'Balance Due',
    scanToUpdateStatus: 'SCAN TO UPDATE STATUS',
    includedShipments: 'Included Shipments',
    statusUpdateButtons: 'Status Update Buttons',
    markAsSent: 'Mark as Sent',
    markAsPaid: 'Mark as Paid',
    createYourFirstInvoice: 'Create Your First Invoice',
    noInvoicesMatch: 'No invoices match your search criteria.',
    getStartedByCreating: 'Get started by creating your first invoice.',
    enterCustomerCode: 'Enter customer code',
    selectDestinationWarehouse: 'Select destination warehouse',
    requiredForWarehouseUsers: 'Required for warehouse users to see this invoice',
    ratePerKg: 'Rate per kg ($)',
    ratePerPiece: 'Rate per piece ($)',
    ratePerShipment: 'Rate per shipment ($)',
    selectShipmentsForCustomer: 'Select Shipments for Customer',
    selectShipments: 'Select Shipments',
    enterCustomerCodeAbove: 'Enter a customer code above to see available shipments for that customer',
    noShipmentsFoundForCustomer: 'No shipments found for customer',
    shipmentsSelected: 'shipment(s) selected',
    invoicePreview: 'Invoice Preview',
    subtotal: 'Subtotal',
    updateInvoice: 'Update Invoice',
    createInvoice: 'Create Invoice',
    enterCustomerCodeToEnable: 'Enter customer code to enable',
    combineMultipleShipments: 'Combine multiple shipments into a single invoice',
    optionalTripName: 'Trip (Optional)',
    selectTripToFilterShipments: 'Select trip to filter shipments',
    allShipments: 'All Shipments',
    shipments: 'shipments',
    showingShipmentsFor: 'Showing shipments for:',
    customer: 'Customer',
    trip: 'Trip',
    customerNotFoundWithCode: 'Customer not found with code:',
    autoFilledFromCustomerInformation: 'Auto-filled from customer information',
    customerPhone: 'Customer Phone',
    autoFilledFromCustomerCode: 'Auto-filled from customer code',
    customerName: 'Customer Name',
    autoFilledFromTrip: 'Auto-filled from trip',
    noResultsFound: 'No results found. Try another relay?',
    tryAnotherRelay: 'Try another relay?',
    noResultsFoundTryFilters: 'No results found. Try adjusting your filters.',
    noResultsFoundTrySearch: 'No results found. Try a different search term.',
    noResultsFoundTryDateRange: 'No results found. Try a different date range.',
    foundInvoicesBetween: 'Found',
    invoicesBetween: 'invoices between',
    and: 'and',
    pleaseSelectBothDates: 'Please select both start and end dates',
    applyDateFilter: 'Apply Date Filter',
    clear: 'Clear',
    summaryStatistics: 'Summary Statistics',

    // Home Page
    welcomeTo: 'Welcome to',
    professionalSourcing: 'Professional Sourcing & Shipping Solutions',
    homeTagline: 'Your trusted partner for global sourcing, quality inspection, and seamless logistics',
    trackYourShipment: 'Track Your Shipment',
    enterShipmentNumber: 'Enter your shipment number to check the current status',
    shipmentNumber: 'Shipment Number',
    trackShipment: 'Track Shipment',
    freightMethod: 'Freight Method',
    estimatedDelivery: 'Estimated Delivery',
    shipmentProgress: 'Shipment Progress',
    ourServices: 'Our Services',
    airFreight: 'Air Freight',
    airFreightDesc: 'Fast and reliable air cargo services worldwide',
    seaFreight: 'Sea Freight',
    seaFreightDesc: 'FCL & LCL solutions - Cost-effective ocean freight for all shipment sizes',
    sourcing: 'Product Sourcing',
    sourcingDesc: 'Find the best suppliers and manufacturers globally',
    inspection: 'Quality Inspection',
    inspectionDesc: 'Professional quality control and inspection services',
    customsClearance: 'Customs Clearance',
    customsClearanceDesc: 'Expert customs documentation and clearance',
    tracking: 'Shipment Tracking',
    trackingDesc: 'Real-time tracking and monitoring of all shipments',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    contactUs: 'Contact Us',
    requestQuote: 'Request Quote',
    trustedPartner: 'Trusted Partner',
    trustedPartnerDesc: 'Serving businesses worldwide with reliable logistics solutions',
    yearsExperience: 'Years of Experience',
    satisfiedClients: 'Satisfied Clients',
    shipmentsDelivered: 'Shipments Delivered',
    globalNetwork: 'Global Network',
    allRightsReserved: 'All rights reserved',
    totalSubtotal: 'Total Subtotal ($)',
    totalAmount: 'Total Amount ($)',
    totalPaid: 'Total Paid ($)',
    totalBalance: 'Total Balance ($)',
    editInvoice: 'Edit Invoice',
    updateInvoiceDetails: 'Update invoice details and status',
    customerCode: 'Customer Code',
    tripName: 'Trip Name',
    optionalTripName: 'Optional trip name',
    areYouSureDeleteInvoice: 'Are you sure you want to delete this invoice? This action cannot be undone.',
    deleteInvoice: 'Delete Invoice',
    printInvoice: 'Print Invoice',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    close: 'Close',
    submit: 'Submit',
    login: 'Login',
    logout: 'Logout',
    username: 'Username',
    password: 'Password',
    enterUsername: 'Enter username',
    enterPassword: 'Enter password',
    loginSuccessful: 'Login Successful',
    welcomeBack: 'Welcome back!',
    loginFailed: 'Login Failed',
    invalidCredentials: 'Invalid username or password',
    defaultCredentials: 'Default Credentials',
    admin: 'Admin',
    warehouseUser: 'Warehouse User',
    shippingManagementSystem: 'Shipping Management System',
    dashboard: 'Dashboard',
    customers: 'Customers',
    warehouses: 'Warehouses',
    shipments: 'Shipments',
    trips: 'Trips',
    invoices: 'Invoices',
    users: 'Users',
    success: 'Success',
    error: 'Error',
    search: 'Search',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    actions: 'Actions',
    view: 'View',
    close: 'Close',
    submit: 'Submit',
    login: 'Login',
    logout: 'Logout',
    all: 'All',
    filter: 'Filter',
    filters: 'Filters',
    history: 'History',
    print: 'Print',
    download: 'Download',
    markAsSent: 'Mark as Sent',
    markAsPaid: 'Mark as Paid',
    profile: 'Profile',
    settings: 'Settings',
    create: 'Create',
    update: 'Update',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    totalCustomers: 'Total Customers',
    totalWarehouses: 'Total Warehouses',
    activeShipments: 'Active Shipments',
    pendingInvoices: 'Pending Invoices',
    recentActivity: 'Recent Activity',
    quickStats: 'Quick Statistics',
    totalTrips: 'Total Trips',
    active: 'Active',
    inactive: 'Inactive',
    yourWarehouse: 'Your warehouse',
    maintenance: 'Maintenance',
    addCustomer: 'Add Customer',
    customerName: 'Customer Name',
    customerCode: 'Customer Code',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    registrationDate: 'Registration Date',
    noCustomers: 'No customers found',
    addWarehouse: 'Add Warehouse',
    warehouseName: 'Warehouse Name',
    warehouseType: 'Warehouse Type',
    location: 'Location',
    capacity: 'Capacity',
    currentStock: 'Current Stock',
    main: 'Main',
    branch: 'Branch',
    distribution: 'Distribution',
    noWarehouses: 'No warehouses found',
    addShipment: 'Add Shipment',
    trackingNumber: 'Tracking Number',
    customer: 'Customer',
    origin: 'Origin',
    destination: 'Destination',
    weight: 'Weight',
    volume: 'Volume',
    packages: 'Packages',
    status: 'Status',
    pending: 'Pending',
    noShipments: 'No shipments found',
    allShipments: 'All Shipments',
    processing: 'Processing',
    atDestination: 'At Destination',
    addTrip: 'Add Trip',
    tripNumber: 'Trip Number',
    driver: 'Driver',
    vehicle: 'Vehicle',
    departure: 'Departure',
    arrival: 'Arrival',
    totalWeight: 'Total Weight',
    totalVolume: 'Total Volume',
    totalPackages: 'Total Packages',
    noTrips: 'No trips found',
    allTrips: 'All Trips',
    planning: 'Planning',
    completed: 'Completed',
    addInvoice: 'Add Invoice',
    invoiceNumber: 'Invoice Number',
    date: 'Date',
    dueDate: 'Due Date',
    subtotal: 'Subtotal',
    tax: 'Tax',
    total: 'Total',
    paymentStatus: 'Payment Status',
    draft: 'Draft',
    sent: 'Sent',
    paid: 'Paid',
    overdue: 'Overdue',
    noInvoices: 'No invoices found',
    allInvoices: 'All Invoices',
    partial: 'Partial',
    
    // New translations needed
    welcomeBackTitle: 'Welcome Back',
    forgotPassword: 'Forgot password?',
    dontHaveAccount: "Don't have an account?",
    registerHere: 'Register here',
    alreadyHaveAccount: 'Already have an account?',
    signInHere: 'Sign in here',
    createAccount: 'Create Account',
    verifyEmail: 'Verify Email',
    reports: 'Reports',
    scan: 'Scan',
    registerForSourceMyGoods: 'Register for SourceMyGoods to manage your shipments',
    enterVerificationCode: 'Enter the verification code sent to',
    comprehensiveLogistics: 'Comprehensive logistics solutions tailored to your business needs',
    readyToStreamline: 'Ready to streamline your global sourcing?',
    joinThousands: 'Join thousands of satisfied businesses who trust SourceMyGoods for their logistics needs',
    
    // Customer Dashboard
    welcome: 'Welcome',
    customerFallback: 'Customer',
    yourShipments: 'Your shipments',
    onTheWay: 'On the way',
    yourInvoices: 'Your invoices',
    amountDue: 'Amount due',
    visualBreakdownShipments: 'Visual breakdown of your shipments by status',
    details: 'Details',
    
    // Contact Dialog
    getInTouch: 'Get in Touch',
    chooseContactMethod: "Choose your preferred contact method. We're here to help with all your sourcing and shipping needs.",
    emailUs: 'Email Us',
    responseWithin24Hours: 'Response within 24 hours',
    emailCopied: '✓ Email Copied!',
    emailAddressCopied: 'Email address copied to clipboard',
    callUs: 'Call Us',
    directPhoneSupport: 'Direct phone support',
    phoneCopied: '✓ Phone Copied!',
    phoneNumberCopied: 'Phone number copied to clipboard',
    callNow: 'Call Now',
    whatsappBusiness: 'WhatsApp Business',
    chatWithTeam: 'Chat with our team instantly',
    openWhatsApp: 'Open WhatsApp',
    businessHours: 'Business Hours',
    businessHoursTime: 'Monday - Friday: 9:00 AM - 6:00 PM (China Time)',
    sendQuickMessage: 'Send Quick Message',
    yourName: 'Your Name',
    yourEmail: 'Your Email',
    message: 'Message',
    tellUsAboutNeeds: 'Tell us about your sourcing or shipping needs...',
    sendMessage: 'Send Message',
    opensEmailClient: 'This will open your default email application',
    openingEmailClient: 'Opening email client...',
    defaultEmailWillOpen: 'Your default email application will open',

    // Dashboard (Admin/Warehouse) hardcoded strings
    dashboardTitle: 'Dashboard',
    overviewOfOperations: 'Overview of your shipping operations',
    exportPdf: 'Export PDF',
    exportExcel: 'Export Excel',
    dateRangeFilter: 'Date Range Filter',
    filterDashboardByDate: 'Filter dashboard data by date range',
    startDate: 'Start Date',
    endDate: 'End Date',
    applyFilter: 'Apply Filter',
    clearFilter: 'Clear Filter',
    activeShipmentsInSystem: 'Active shipments in system',
    registeredCustomerAccounts: 'Registered customer accounts',
    totalWarehouses: 'Total Warehouses',
    yourWarehouseSingle: 'Your warehouse',
    totalWarehousesLabel: 'Total warehouses',
    currentlyActiveTrips: 'Currently active trips',
    awaitingPayment: 'Awaiting payment',
    totalRevenueEarned: 'Total revenue earned',
    revenueByWarehouseLabel: 'Revenue by Warehouse:',
    revenueBreakdown: 'Revenue Breakdown:',
    shipmentStatusDistribution: 'Shipment Status Distribution',
    visualBreakdownAllShipments: 'Visual breakdown of all shipments by status',
    invoiceStatusOverview: 'Invoice Status Overview',
    invoiceDistributionPaymentStatus: 'Current invoice distribution and payment status',
    noRecentShipments: 'No recent shipments',
    detailedViewShipmentsStatus: 'Detailed view of shipments with this status',
    detailedViewCustomersByStatus: 'Detailed view of customers by status',
    detailedViewWarehousesByType: 'Detailed view of warehouses by type',
    detailedViewTripsByStatus: 'Detailed view of trips by status',
    detailedViewInvoicesByStatus: 'Detailed view of invoices by status',
    noItemsFound: 'No items found',
    noItemsFoundInSystem: 'No items found in the system.',
    showing: 'Showing',
    processingShipments: 'Processing Shipments',
    shipmentsInTransit: 'Shipments In Transit',
    shipmentsAtDestination: 'Shipments At Destination',
    deliveredShipments: 'Delivered Shipments',
    tripsLabel: 'Trips',
    invoicesLabel: 'Invoices',
    warehousesLabel: 'Warehouses',
    customersLabel: 'Customers',
    allCustomersLabel: 'All Customers',
    allWarehousesLabel: 'All Warehouses',
    allInvoicesLabel: 'All Invoices',
    sectionDetails: 'Section Details',
    emailField: 'Email:',
    phoneField: 'Phone:',
    locationField: 'Location:',
    shipmentsField: 'Shipments:',
    codeField: 'Code:',
    addressField: 'Address:',
    managerField: 'Manager:',
    capacityField: 'Capacity:',
    currentField: 'Current:',
    units: 'units',
    driverField: 'Driver:',
    vehicleField: 'Vehicle:',
    originField: 'Origin:',
    weightField: 'Weight:',
    destinationField: 'Destination:',
    shipmentsFieldShort: 'Shipments:',
    packagesField: 'Packages:',
    createdField: 'Created:',
    estArrival: 'Est. Arrival:',
    customerField: 'Customer:',
    dueField: 'Due:',
    tripField: 'Trip:',
    volumeField: 'Volume:',
    freightField: 'Freight:',
    estDelivery: 'Est. Delivery:',
    unassigned: 'Unassigned',

    // Sourcing Requests
    sourcingRequests: 'Sourcing Requests',
    requestProductsAndReceiveQuotes: 'Request products and receive quotes',
    newRequest: 'New Request',
    createSourcingRequest: 'Create Sourcing Request',
    tellUsWhatYouNeed: 'Tell us what you need and we\'ll find the best suppliers for you',
    totalRequests: 'Total Requests',
    allTimeRequests: 'All time requests',
    pending: 'Pending',
    awaitingQuote: 'Awaiting quote',
    quoted: 'Quoted',
    awaitingApproval: 'Awaiting approval',
    sourcingRevenue: 'Sourcing Revenue',
    thisMonth: 'This month',
    searchByRequestNumber: 'Search by request number, product name...',
    searchByRequestNumberCustomer: 'Search by request number, product name, customer...',
    filterByCustomer: 'Filter by customer',
    allCustomers: 'All Customers',
    request: 'request',
    requests: 'requests',
    loadingRequests: 'Loading requests...',
    noSourcingRequestsFound: 'No sourcing requests found',
    createYourFirstRequest: 'Create Your First Request',
    items: 'Items',
    item: 'item',
    quantity: 'Quantity',
    unit: 'Unit',
    referenceLink: 'reference link',
    referenceLinks: 'reference links',
    pricePerUnit: 'Price per unit',
    grandTotal: 'Grand Total',
    view: 'View',
    approve: 'Approve',
    quote: 'Quote',
    updateStatus: 'Update Status',
    deleteRequest: 'Delete Request',
    printDownload: 'Print/Download',
    underReview: 'Under Review',
    waitingForPayment: 'Waiting for Payment',
    paymentReceived: 'Payment Received',
    completed: 'Completed',
    cancelled: 'Cancelled',
    
    // Multi-Item Sourcing Form
    productName: 'Product Name',
    productNamePlaceholder: 'e.g., Wireless Headphones',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Detailed description, specifications, requirements...',
    targetPriceOptional: 'Target Price (Optional)',
    targetPricePlaceholder: 'Your budget per unit',
    productPhotos: 'Product Photos',
    uploadOrDragPhotos: 'Upload or drag photos here',
    uploadPhotos: 'Upload Photos',
    clickOrDrag: 'Click to upload or drag and drop',
    maxFileSize: 'Max file size: 10MB',
    addReferenceLinks: 'Add Reference Links',
    addFromPlatforms: 'Add product links from popular platforms',
    pasteProductLink: 'Paste product link (e.g., from Amazon, Alibaba...)',
    selectPlatform: 'Select platform',
    addLink: 'Add Link',
    removeItem: 'Remove Item',
    addAnotherItem: 'Add Another Item',
    generalRequestInfo: 'General Request Info',
    appliestoEntireOrder: 'Applies to entire order',
    specialRequirementsOptional: 'Special Requirements (Optional)',
    packagingCertifications: 'Packaging, certifications, shipping preferences...',
    expectedDeliveryDate: 'Expected Delivery Date',
    selectDate: 'Select date',
    submitSourcingRequest: 'Submit Sourcing Request',
    submittingRequest: 'Submitting Request...',
    atLeastOneItemRequired: 'At least one item is required',
    pleaseCompleteAllFields: 'Please complete all required fields for all items',

    // Footer
    quickLinks: 'Quick Links',
    ourServices: 'Our Services',
    termsAndConditions: 'Terms & Conditions',
    allRightsReservedFull: '© {year} SourceMyGoods. All rights reserved.',
    operatedBy: 'Operated by',
    footerTagline: 'Professional Sourcing, Inspection & Global Shipping Solutions',

    // Register page
    personalInformationSection: 'Personal Information',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Enter your full name',
    emailPlaceholder: 'your.email@example.com',
    phoneNumber: 'Phone Number',
    phoneNumberHint: 'Enter phone number without country code',
    addressInformation: 'Address Information',
    streetAddress: 'Street Address',
    streetAddressPlaceholder: '123 Main Street',
    selectCountry: 'Select a country',
    stateProvince: 'State/Province',
    selectState: 'Select state',
    selectCountryFirst: 'Select country first',
    noStatesAvailable: 'No states available',
    selectCity: 'Select city',
    selectStateFirst: 'Select state first',
    noCitiesAvailable: 'No cities available',
    shippingPreferences: 'Shipping Preferences',
    preferredDestinationWarehouse: 'Preferred Destination Warehouse',
    selectWarehouse: 'Select a warehouse',
    noWarehousesAvailable: 'No warehouses available',
    defaultWarehouseHint: 'Choose the warehouse where you want your shipments delivered by default',
    accountInformationSection: 'Account Information',
    chooseUsername: 'Choose a username',
    createPassword: 'Create a password',
    confirmPasswordPlaceholder: 'Confirm your password',
    iAcceptThe: 'I accept the',
    andAcknowledge: 'and acknowledge that my information will be used in accordance with SourceMyGoods\' privacy practices.',
    continueToVerification: 'Continue to Verification',
    sendingCode: 'Sending Code...',
    verifyAndCreateAccount: 'Verify & Create Account',
    creatingAccount: 'Creating Account...',
    checkEmailInbox: 'Check your email inbox',
    sentSixDigitCode: 'We\'ve sent a 6-digit verification code to',
    checkSpamFolder: 'Check your spam/junk folder',
    spamFolderHint: 'if you don\'t see the email in your inbox. The code expires in 10 minutes.',
    verificationCodeLabel: 'Verification Code',
    enterSixDigitCode: 'Enter 6-digit code',
    codeSentTo: 'Code sent to',
    back: '← Back',
    resendCode: 'Resend Code',
    sending: 'Sending...',
    mustAcceptTerms: 'You must accept the Terms & Conditions to register',
    passwordsDoNotMatchError: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 6 characters',
    emailAlreadyRegistered: 'This email is already registered. Please use a different email or try logging in.',
    verificationCodeSent: 'Verification Code Sent!',
    sixDigitCodeSentTo: 'A 6-digit code has been sent to',
    invalidVerificationCode: 'Invalid verification code. Please check and try again.',
    verificationCodeExpired: 'Verification code has expired. Please request a new one.',
    accountCreated: 'Account Created!',
    accountCreatedDesc: 'Welcome! Your customer code is: {code}. You can now login.',
    registrationFailed: 'Registration failed. Please try again.',

    // Terms & Conditions page
    termsTitle: 'Terms & Conditions',
    lastUpdated: 'Last updated:',
    agreementToTerms: 'Agreement to Terms',
    termsSection1Title: '1. Introduction',
    termsSection1P1: 'Welcome to SourceMyGoods ("Company", "we", "our", "us"). These Terms and Conditions ("Terms", "Terms and Conditions") govern your use of our shipping and logistics management platform and services.',
    termsSection1P2: 'By accessing or using SourceMyGoods, you agree to be bound by these Terms. If you disagree with any part of these terms, you may not access the service.',
    termsSection2Title: '2. Use of Service',
    termsSection2Intro: 'Our platform provides sourcing, inspection, shipping, and logistics management services. You agree to:',
    termsSection2Li1: 'Provide accurate and complete registration information',
    termsSection2Li2: 'Maintain the security of your account credentials',
    termsSection2Li3: 'Use the service only for lawful purposes',
    termsSection2Li4: 'Not interfere with or disrupt the service',
    termsSection2Li5: 'Comply with all applicable laws and regulations',
    termsSection3Title: '3. Account Registration',
    termsSection3Intro: 'When you create an account with us, you must:',
    termsSection3Li1: 'Be at least 18 years of age',
    termsSection3Li2: 'Provide truthful and accurate information',
    termsSection3Li3: 'Maintain and promptly update your account information',
    termsSection3Li4: 'Maintain the confidentiality of your password',
    termsSection3Li5: 'Accept responsibility for all activities under your account',
    termsSection4Title: '4. Services & Fees',
    termsSection4Intro: 'SourceMyGoods provides the following services:',
    termsSection4Li1: 'Global product sourcing and supplier verification',
    termsSection4Li2: 'Quality inspection and assurance services',
    termsSection4Li3: 'Air and sea freight shipping',
    termsSection4Li4: 'Customs clearance assistance',
    termsSection4Li5: 'Warehouse and inventory management',
    termsSection4Li6: 'Shipment tracking and management',
    termsSection4P2: 'Fees for services will be clearly communicated before any transaction. You agree to pay all applicable fees and charges incurred under your account.',
    termsSection5Title: '5. Shipping & Delivery',
    termsSection5Intro: 'We strive to provide accurate delivery estimates, but:',
    termsSection5Li1: 'Delivery times are estimates and not guaranteed',
    termsSection5Li2: 'International shipments may be subject to customs delays',
    termsSection5Li3: 'You are responsible for providing accurate delivery information',
    termsSection5Li4: 'Risk of loss transfers to you upon delivery to the carrier',
    termsSection6Title: '6. Liability & Insurance',
    termsSection6P1: 'We recommend purchasing shipping insurance for valuable items. Our liability for lost or damaged goods is limited to the declared value or applicable carrier limits, whichever is lower.',
    termsSection7Title: '7. Prohibited Items',
    termsSection7Intro: 'You may not ship:',
    termsSection7Li1: 'Illegal or regulated substances',
    termsSection7Li2: 'Hazardous materials without proper documentation',
    termsSection7Li3: 'Items prohibited by carrier policies',
    termsSection7Li4: 'Counterfeit or infringing goods',
    termsSection7Li5: 'Items restricted by destination country laws',
    termsSection8Title: '8. Privacy & Data Protection',
    termsSection8P1: 'We collect and process your personal information in accordance with our Privacy Policy. By using our services, you consent to such collection and processing.',
    termsSection9Title: '9. Intellectual Property',
    termsSection9P1: 'The SourceMyGoods platform, including all content, features, and functionality, is owned by us and protected by copyright, trademark, and other intellectual property laws.',
    termsSection10Title: '10. Termination',
    termsSection10P1: 'We reserve the right to terminate or suspend your account immediately, without prior notice, for violations of these Terms or for any other reason at our sole discretion.',
    termsSection11Title: '11. Limitation of Liability',
    termsSection11P1: 'To the maximum extent permitted by law, SourceMyGoods shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the service.',
    termsSection12Title: '12. Changes to Terms',
    termsSection12P1: 'We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page and updating the "Last updated" date.',
    termsSection13Title: '13. Contact Information',
    termsSection13Intro: 'If you have any questions about these Terms, please contact us at:',
    termsFooterNote: 'By creating an account and using SourceMyGoods, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.',
    backToRegistration: '← Back to Registration',
  },
  ar: {
    // Navigation
    shippingManagementSystem: 'نظام إدارة الشحن',
    dashboard: 'لوحة التحكم',
    customers: 'العملاء',
    warehouses: 'المستودعات',
    shipments: 'الشحنات',
    trips: 'الرحلات',
    invoices: 'الفواتير',
    users: 'المستخدمون',
    success: 'نجح',
    error: 'خطأ',

    // Common Actions
    home: 'الرئيسية',
    search: 'بحث',
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    defaultCredentials: 'Default Credentials',
    changeDefaultLoginCredentials: 'Change default admin login credentials',
    defaultUsername: 'Default Username',
    defaultPassword: 'Default Password',
    newDefaultUsername: 'New Default Username',
    newDefaultPassword: 'New Default Password',
    saveDefaultCredentials: 'Save Default Credentials',
    defaultCredentialsUpdated: 'Default Credentials Updated',
    defaultCredentialsUpdatedDesc: 'Default admin credentials have been updated successfully.',
    accountType: 'نوع الحساب',
    customerCode: 'رمز العميل:',
    status: 'الحالة',
    warehouseCode: 'رمز المستودع',
    warehouseName: 'اسم المستودع',
    active: 'نشط',
    actions: 'الإجراءات',
    view: 'عرض',
    close: 'إغلاق',
    submit: 'إرسال',
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    register: 'تسجيل',

    // Additional UI elements
    all: 'الكل',
    filter: 'فلتر',
    filters: 'المرشحات',
    history: 'التاريخ',
    print: 'طباعة',
    download: 'تحميل',
    markAsSent: 'تعليم كمرسل',
    markAsPaid: 'تعليم كمدفوع',
    profile: 'الملف الشخصي',
    settings: 'الإعدادات',
    create: 'إنشاء',
    update: 'تحديث',
    confirm: 'تأكيد',
    yes: 'نعم',
    no: 'لا',

    // Additional UI elements
    all: 'الكل',
    filter: 'فلتر',
    filters: 'المرشحات',
    history: 'التاريخ',
    print: 'طباعة',
    download: 'تحميل',
    markAsSent: 'تعليم كمرسل',
    markAsPaid: 'تعليم كمدفوع',
    profile: 'الملف الشخصي',
    settings: 'الإعدادات',
    create: 'إنشاء',
    update: 'تحديث',
    confirm: 'تأكيد',
    yes: 'نعم',
    no: 'لا',

    // Login
    username: 'اسم المستخدم',
    usernameOrEmail: 'اسم المستخدم أو البريد الإلكتروني',
    enterUsernameOrEmail: 'أدخل اسم المستخدم أو البريد الإلكتروني',
    password: 'كلمة المرور',
    enterUsername: 'أدخل اسم المستخدم',
    enterPassword: 'أدخل كلمة المرور',
    loginSuccessful: 'تم تسجيل الدخول بنجاح',
    welcomeBack: 'مرحبا بعودتك!',
    loginFailed: 'فشل تسجيل الدخول',
    invalidCredentials: 'اسم المستخدم أو كلمة المرور غير صحيحة',
    defaultCredentials: 'بيانات الدخول الافتراضية',
    admin: 'المسؤول',
    warehouseUser: 'مستخدم المستودع',

    // Dashboard
    totalCustomers: 'إجمالي العملاء',
    totalWarehouses: 'إجمالي المستودعات',
    activeShipments: 'الشحنات النشطة',
    pendingInvoices: 'الفواتير المعلقة',
    recentActivity: 'النشاط الأخير',
    quickStats: 'إحصائيات سريعة',
    totalTrips: 'إجمالي الرحلات',
    active: 'نشط',
    inactive: 'غير نشط',
    yourWarehouse: 'مستودعك',
    maintenance: 'صيانة',

    // Customers
    addCustomer: 'إضافة عميل',
    customerName: 'اسم العميل',
    customerCode: 'رمز العميل: ',
    manageCustomerAccounts: 'إدارة حسابات العملاء مع رموز مكونة من 4 أرقام',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    address: 'العنوان',
    registrationDate: 'تاريخ التسجيل',
    noCustomers: 'لا يوجد عملاء',

    // Warehouses
    addWarehouse: 'إضافة مستودع',
    warehouseName: 'اسم المستودع',
    warehouseType: 'نوع المستودع',
    location: 'الموقع',
    capacity: 'السعة',
    currentStock: 'المخزون الحالي',
    main: 'رئيسي',
    branch: 'فرع',
    distribution: 'توزيع',
    noWarehouses: 'لا يوجد مستودعات',
    ourWarehouseLocations: 'مواقع مستودعاتنا',
    contactInfoForWarehouses: 'معلومات الاتصال لمستودعاتنا',
    mainWarehouse: 'المستودع الرئيسي',
    warehouseManager: 'مدير المستودع',
    notAssigned: 'غير معين',
    noWarehouseInfoAvailable: 'لا توجد معلومات مستودع متاحة',

    // Shipments
    addShipment: 'إضافة شحنة',
    createShipment: 'إنشاء شحنة',
    trackingNumber: 'رقم التتبع',
    customer: 'العميل',
    trackAndManageShipments: 'تتبع وإدارة جميع الشحنات مع تحديثات الحالة في الوقت الفعلي',
    origin: 'المصدر',
    destination: 'الوجهة',
    weight: 'الوزن',
    volume: 'الحجم',
    packages: 'الطرود',
    status: 'الحالة',
    pending: 'قيد الانتظار',
    inTransit: 'في الطريق',
    delivered: 'تم التسليم',
    noShipments: 'لا يوجد شحنات',
    allShipments: 'كل الشحنات',
    processing: 'قيد المعالجة',
    atDestination: 'في الوجهة',

    // Trips
    addTrip: 'إضافة رحلة',
    tripNumber: 'رقم الرحلة',
    driver: 'السائق',
    vehicle: 'المركبة',
    departure: 'المغادرة',
    arrival: 'الوصول',
    totalWeight: 'الوزن الإجمالي',
    totalVolume: 'الحجم الإجمالي',
    totalPackages: 'إجمالي الطرود',
    noTrips: 'لا يوجد رحلات',
    allTrips: 'كل الرحلات',
    planning: 'التخطيط',
    completed: 'مكتملة',

    // Invoices
    addInvoice: 'إضافة فاتورة',
    invoiceNumber: 'رقم الفاتورة',
    date: 'التاريخ',
    dueDate: 'تاريخ الاستحقاق',
    subtotal: 'المجموع الفرعي',
    tax: 'الضريبة',
    total: 'الإجمالي',
    paymentStatus: 'حالة الدفع',
    draft: 'مسودة',
    sent: 'مرسل',
    paid: 'مدفوع',
    overdue: 'متأخر',
    noInvoices: 'لا يوجد فواتير',
    allInvoices: 'كل الفواتير',
    partial: 'جزئي',
    viewDraftInvoices: 'عرض جميع الفواتير التي لا تزال في حالة المسودة',
    viewSentInvoices: 'عرض جميع الفواتير التي تم إرسالها إلى العملاء',
    viewPaidInvoices: 'عرض جميع الفواتير التي تم دفعها بالكامل',
    viewOverdueInvoices: 'عرض جميع الفواتير التي تجاوزت تاريخ الاستحقاق',
    viewPartialInvoices: 'عرض جميع الفواتير التي تم دفعها جزئياً',
    generateCombinedInvoices: 'إنشاء فواتير مجتمعة لشحنات متعددة',

    // Customer Dashboard
    welcomeCustomer: 'مرحباً {customerName}!',
    customerCode: 'رمز العميل: ',
    totalShipments: 'إجمالي الشحنات',
    inTransit: 'في الطريق',
    totalInvoices: 'إجمالي الفواتير',
    outstanding: 'المتبقي',
    shipmentStatus: 'حالة الشحنات',
    currentStatusOfShipments: 'الحالة الحالية لجميع شحناتك',
    processing: 'قيد المعالجة',
    atDestination: 'في الوجهة',
    delivered: 'تم التسليم',
    recentShipments: 'الشحنات الأخيرة',
    viewAll: 'عرض الكل',
    latestShipmentActivity: 'أحدث نشاط الشحن',
    noShipmentsYet: 'لا توجد شحنات بعد',
    recentInvoices: 'الفواتير الأخيرة',
    latestInvoiceActivity: 'أحدث نشاط الفواتير',
    noInvoicesYet: 'لا توجد فواتير بعد',
    amount: 'المبلغ',
    quickActions: 'إجراءات سريعة',
    commonTasksAndShortcuts: 'مهام شائعة واختصارات',
    viewMyShipments: 'عرض شحناتي',
    viewMyInvoices: 'عرض فواتيري',
    myProfile: 'ملفي الشخصي',
    notAvailable: 'غير متوفر',

    // Customer Profile
    myProfile: 'ملفي الشخصي',
    adminAccountInformation: 'معلومات حساب المسؤول',
    adminAccount: 'معلومات حساب المسؤول',
    warehouseManagerAccountInformation: 'معلومات حساب مدير المستودع',
    warehouseManagerAccount: 'معلومات حساب مدير المستودع',
    customerAccountInformation: 'معلومات حساب العميل',
    customerAccount: 'معلومات حساب العميل',
    administratorAccountDetails: 'تفاصيل حساب المسؤول',
    warehouseManagerAccountDetails: 'تفاصيل حساب مدير المستودع',
    customerAccountDetails: 'تفاصيل حساب العميل',
    accountInformation: 'معلومات الحساب',
    username: 'اسم المستخدم',
    editProfile: 'تعديل الملف الشخصي',
    changePassword: 'تغيير كلمة المرور',
    personalInformation: 'المعلومات الشخصية',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    address: 'العنوان',
    city: 'المدينة',
    state: 'الولاية',
    country: 'البلد',
    destinationWarehouse: 'مستودع الوجهة',
    warehouseInformation: 'معلومات المستودع',
    warehouseName: 'اسم المستودع',
    warehouseCode: 'رمز المستودع',
    warehouseType: 'نوع المستودع',
    warehouseLocation: 'موقع المستودع',
    warehouseCapacity: 'سعة المستودع',
    warehouseCurrentStock: 'المخزون الحالي',
    warehouseStatus: 'حالة المستودع',
    security: 'الأمان',
    changeYourPassword: 'تغيير كلمة المرور',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور',
    passwordsDoNotMatch: 'كلمات المرور غير متطابقة',
    passwordChangedSuccessfully: 'تم تغيير كلمة المرور بنجاح',
    profileUpdatedSuccessfully: 'تم تحديث الملف الشخصي بنجاح',
    saveChanges: 'حفظ التغييرات',
    cancel: 'إلغاء',

    // Dashboard
    totalRevenue: 'الإيرادات الإجمالية',
    revenueByWarehouse: 'الإيرادات حسب المستودع',
    shipmentsByStatus: 'الشحنات حسب الحالة',
    tripsByStatus: 'الرحلات حسب الحالة',
    invoicesByStatus: 'الفواتير حسب الحالة',
    warehousesByType: 'المستودعات حسب النوع',
    warehousesByStatus: 'المستودعات حسب الحالة',
    customersByStatus: 'العملاء حسب الحالة',
    recentShipments: 'الشحنات الأخيرة',
    latestShipmentActivities: 'أحدث أنشطة الشحن وتحديثات الحالة',
    processing: 'قيد المعالجة',
    inTransit: 'في الطريق',
    atDestination: 'في الوجهة',
    delivered: 'تم التسليم',
    planning: 'التخطيط',
    completed: 'مكتملة',
    main: 'رئيسي',
    branch: 'فرع',
    distribution: 'توزيع',
    active: 'نشط',
    inactive: 'غير نشط',
    maintenance: 'صيانة',
    totalShipments: 'إجمالي الشحنات',
    activeTrips: 'الرحلات النشطة',
    pendingInvoices: 'الفواتير المعلقة',
    totalCustomers: 'إجمالي العملاء',
    totalWarehouses: 'إجمالي المستودعات',
    totalTrips: 'إجمالي الرحلات',
    quickStats: 'إحصائيات سريعة',
    revenue: 'الإيرادات',
    percentage: 'النسبة المئوية',
    customerInformation: 'معلومات العميل',
    generatedOn: 'تم إنشاؤه في',
    thankYou: 'شكراً لك على عملك!',
    billTo: 'فاتورة لـ',
    invoiceDetails: 'تفاصيل الفاتورة',
    issueDate: 'تاريخ الإصدار',
    paidDate: 'تاريخ الدفع',
    pricingModel: 'نموذج التسعير',
    shipmentDetails: 'تفاصيل الشحنة',
    no: 'رقم',
    description: 'الوصف',
    packages: 'الطرود',
    pricePerKg: 'السعر (لكل كجم)',
    pricePerPiece: 'السعر (لكل قطعة)',
    pricePerShipment: 'السعر (لكل شحنة)',
    amount: 'المبلغ',
    additionalItems: 'عناصر إضافية',
    discount: 'خصم',
    grandTotal: 'الإجمالي',
    amountPaid: 'المدفوع',
    balanceDue: 'المتبقي',
    scanToUpdateStatus: 'امسح لتحديث الحالة',
    includedShipments: 'الشحنات المضمنة',
    statusUpdateButtons: 'أزرار تحديث الحالة',
    markAsSent: 'تعليم كمرسل',
    markAsPaid: 'تعليم كمدفوع',
    createYourFirstInvoice: 'أنشئ فاتورتك الأولى',
    noInvoicesMatch: 'لا توجد فواتير تطابق معايير البحث.',
    getStartedByCreating: 'ابدأ بإنشاء فاتورتك الأولى.',
    enterCustomerCode: 'أدخل رمز العميل',
    selectDestinationWarehouse: 'اختر مستودع الوجهة',
    requiredForWarehouseUsers: 'مطلوب لمستخدمي المستودعات لرؤية هذه الفاتورة',
    ratePerKg: 'السعر لكل كجم ($)',
    ratePerPiece: 'السعر لكل قطعة ($)',
    ratePerShipment: 'السعر لكل شحنة ($)',
    selectShipmentsForCustomer: 'اختر الشحنات للعميل',
    selectShipments: 'اختر الشحنات',
    enterCustomerCodeAbove: 'أدخل رمز العميل أعلاه لرؤية الشحنات المتاحة لهذا العميل',
    noShipmentsFoundForCustomer: 'لا توجد شحنات للعميل',
    shipmentsSelected: 'شحنة/شحنات محددة',
    invoicePreview: 'معاينة الفاتورة',
    subtotal: 'المجموع الفرعي',
    updateInvoice: 'تحديث الفاتورة',
    createInvoice: 'إنشاء الفاتورة',
    enterCustomerCodeToEnable: 'أدخل رمز العميل للتفعيل',
    combineMultipleShipments: 'دمج شحنات متعددة في فاتورة واحدة',
    optionalTripName: 'الرحلة (اختياري)',
    selectTripToFilterShipments: 'اختر رحلة لتصفية الشحنات',
    allShipments: 'كل الشحنات',
    shipments: 'شحنات',
    showingShipmentsFor: 'عرض الشحنات لـ:',
    customer: 'العميل',
    trip: 'الرحلة',
    customerNotFoundWithCode: 'العميل غير موجود بالرمز:',
    autoFilledFromCustomerInformation: 'معبأ تلقائياً من معلومات العميل',
    customerPhone: 'هاتف العميل',
    autoFilledFromCustomerCode: 'معبأ تلقائياً من رمز العميل',
    customerName: 'اسم العميل',
    autoFilledFromTrip: 'معبأ تلقائياً من الرحلة',
    noResultsFound: 'لم يتم العثور على نتائج. جرب مستودعاً آخر؟',
    tryAnotherRelay: 'جرب مستودعاً آخر؟',
    noResultsFoundTryFilters: 'لم يتم العثور على نتائج. حاول تعديل المرشحات.',
    noResultsFoundTrySearch: 'لم يتم العثور على نتائج. جرب مصطلح بحث مختلف.',
    noResultsFoundTryDateRange: 'لم يتم العثور على نتائج. جرب نطاق تاريخ مختلف.',
    foundInvoicesBetween: 'تم العثور على',
    invoicesBetween: 'فاتورة/فواتير بين',
    and: 'و',
    pleaseSelectBothDates: 'الرجاء تحديد تاريخ البدء والانتهاء',
    applyDateFilter: 'تطبيق مرشح التاريخ',
    clear: 'مسح',
    summaryStatistics: 'إحصائيات ملخصة',
    totalSubtotal: 'المجموع الفرعي ($)',
    totalAmount: 'المبلغ الإجمالي ($)',
    totalPaid: 'الإجمالي المدفوع ($)',
    totalBalance: 'الرصيد المتبقي ($)',
    editInvoice: 'تعديل الفاتورة',
    updateInvoiceDetails: 'تحديث تفاصيل الفاتورة والحالة',
    customerCode: 'رمز العميل: ',
    tripName: 'اسم الرحلة',
    optionalTripName: 'اسم الرحلة (اختياري)',
    areYouSureDeleteInvoice: 'هل أنت متأكد من حذف هذه الفاتورة؟ لا يمكن التراجع عن هذا الإجراء.',
    deleteInvoice: 'حذف الفاتورة',
    printInvoice: 'طباعة الفاتورة',
    edit: 'تعديل',
    delete: 'حذف',
    view: 'عرض',
    close: 'إغلاق',
    submit: 'إرسال',
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    enterUsername: 'أدخل اسم المستخدم',
    enterPassword: 'أدخل كلمة المرور',
    loginSuccessful: 'تم تسجيل الدخول بنجاح',
    welcomeBack: 'مرحباً بعودتك!',
    loginFailed: 'فشل تسجيل الدخول',
    invalidCredentials: 'اسم المستخدم أو كلمة المرور غير صحيحة',
    defaultCredentials: 'بيانات الدخول الافتراضية',
    admin: 'المسؤول',
    warehouseUser: 'مستخدم المستودع',
    shippingManagementSystem: 'نظام إدارة الشحن',
    dashboard: 'لوحة التحكم',
    customers: 'العملاء',
    warehouses: 'المستودعات',
    shipments: 'الشحنات',
    trips: 'الرحلات',
    invoices: 'الفواتير',
    users: 'المستخدمون',
    success: 'نجح',
    error: 'خطأ',
    search: 'بحث',
    add: 'إضافة',
    edit: 'تعديل',
    delete: 'حذف',
    save: 'حفظ',
    cancel: 'إلغاء',
    actions: 'الإجراءات',
    view: 'عرض',
    close: 'إغلاق',
    submit: 'إرسال',
    login: 'تسجيل الدخول',
    logout: 'تسجيل الخروج',
    all: 'الكل',
    filter: 'فلتر',
    filters: 'المرشحات',
    history: 'التاريخ',
    print: 'طباعة',
    download: 'تحميل',
    markAsSent: 'تعليم كمرسل',
    markAsPaid: 'تعليم كمدفوع',
    profile: 'الملف الشخصي',
    settings: 'الإعدادات',
    create: 'إنشاء',
    update: 'تحديث',
    confirm: 'تأكيد',
    yes: 'نعم',
    no: 'لا',
    totalCustomers: 'إجمالي العملاء',
    totalWarehouses: 'إجمالي المستودعات',
    activeShipments: 'الشحنات النشطة',
    pendingInvoices: 'الفواتير المعلقة',
    recentActivity: 'النشاط الأخير',
    quickStats: 'إحصائيات سريعة',
    totalTrips: 'إجمالي الرحلات',
    active: 'نشط',
    inactive: 'غير نشط',
    yourWarehouse: 'مستودعك',
    maintenance: 'صيانة',
    addCustomer: 'إضافة عميل',
    customerName: 'اسم العميل',
    customerCode: 'رمز العميل',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    address: 'العنوان',
    registrationDate: 'تاريخ التسجيل',
    noCustomers: 'لا يوجد عملاء',
    addWarehouse: 'إضافة مستودع',
    warehouseName: 'اسم المستودع',
    warehouseType: 'نوع المستودع',
    location: 'الموقع',
    capacity: 'السعة',
    currentStock: 'المخزون الحالي',
    main: 'رئيسي',
    branch: 'فرع',
    distribution: 'توزيع',
    noWarehouses: 'لا يوجد مستودعات',
    addShipment: 'إضافة شحنة',
    trackingNumber: 'رقم التتبع',
    customer: 'العميل',
    origin: 'المصدر',
    destination: 'الوجهة',
    weight: 'الوزن',
    volume: 'الحجم',
    packages: 'الطرود',
    status: 'الحالة',
    pending: 'قيد الانتظار',
    noShipments: 'لا يوجد شحنات',
    allShipments: 'كل الشحنات',
    processing: 'قيد المعالجة',
    atDestination: 'في الوجهة',
    addTrip: 'إضافة رحلة',
    tripNumber: 'رقم الرحلة',
    driver: 'السائق',
    vehicle: 'المركبة',
    departure: 'المغادرة',
    arrival: 'الوصول',
    totalWeight: 'الوزن الإجمالي',
    totalVolume: 'الحجم الإجمالي',
    totalPackages: 'إجمالي الطرود',
    noTrips: 'لا يوجد رحلات',
    allTrips: 'كل الرحلات',
    planning: 'التخطيط',
    completed: 'مكتملة',
    addInvoice: 'إضافة فاتورة',
    invoiceNumber: 'رقم الفاتورة',
    date: 'التاريخ',
    dueDate: 'تاريخ الاستحقاق',
    subtotal: 'المجموع الفرعي',
    tax: 'الضريبة',
    total: 'الإجمالي',
    paymentStatus: 'حالة الدفع',
    draft: 'مسودة',
    sent: 'مرسل',
    paid: 'مدفوع',
    overdue: 'متأخر',
    noInvoices: 'لا يوجد فواتير',
    allInvoices: 'كل الفواتير',
    partial: 'جزئي',

    // Home Page
    welcomeTo: 'مرحباً بك في',
    globalSourcingShipping: 'حلول الشراء والشحن العالمية',
    professionalSourcing: 'الشراء الاحترافي وحلول الشحن',
    homeTagline: 'شريكك الموثوق للشراء العالمي وفحص الجودة والخدمات اللوجستية السلسة',
    trackYourShipment: 'تتبع شحنتك',
    enterShipmentNumber: 'أدخل رقم الشحنة للتحقق من الحالة الحالية',
    shipmentNumber: 'رقم الشحنة',
    trackShipment: 'تتبع الشحنة',
    freightMethod: 'طريقة الشحن',
    estimatedDelivery: 'التسليم المتوقع',
    shipmentProgress: 'تقدم الشحنة',
    ourServices: 'خدماتنا',
    airFreight: 'الشحن الجوي',
    airFreightDesc: 'خدمات شحن جوي سريعة وموثوقة في جميع أنحاء العالم',
    seaFreight: 'الشحن البحري',
    seaFreightDesc: 'حلول FCL و LCL - شحن بحري فعال من حيث التكلفة لجميع أحجام الشحنات',
    sourcing: 'توريد المنتجات',
    sourcingDesc: 'إيجاد أفضل الموردين والمصنعين حول العالم',
    inspection: 'فحص الجودة',
    inspectionDesc: 'مراقبة الجودة الاحترافية وخدمات الفحص',
    customsClearance: 'التخليص الجمركي',
    customsClearanceDesc: 'خبراء في الوثائق الجمركية والتخليص',
    tracking: 'تتبع الشحنات',
    trackingDesc: 'تتبع ومراقبة جميع الشحنات في الوقت الفعلي',
    getStarted: 'ابدأ الآن',
    learnMore: 'اعرف المزيد',
    contactUs: 'اتصل بنا',
    requestQuote: 'طلب عرض سعر',
    trustedPartner: 'شريك موثوق',
    trustedPartnerDesc: 'نخدم الشركات حول العالم بحلول لوجستية موثوقة',
    yearsExperience: 'سنوات الخبرة',
    satisfiedClients: 'عملاء راضون',
    shipmentsDelivered: 'شحنات تم تسليمها',
    globalNetwork: 'شبكة عالمية',
    allRightsReserved: 'جميع الحقوق محفوظة',
    register: 'تسجيل',
    trackAndManageShipments: 'تتبع وإدارة جميع الشحنات مع تحديثات الحالة في الوقت الفعلي',
    manageCustomerAccounts: 'إدارة حسابات العملاء مع رموز فريدة مكونة من 4 أرقام',
    
    // New translations
    welcomeBackTitle: 'مرحباً بعودتك',
    forgotPassword: 'نسيت كلمة المرور؟',
    dontHaveAccount: 'ليس لديك حساب؟',
    registerHere: 'سجل هنا',
    alreadyHaveAccount: 'لديك حساب بالفعل؟',
    signInHere: 'سجل الدخول هنا',
    createAccount: 'إنشاء حساب',
    verifyEmail: 'تحقق من البريد الإلكتروني',
    reports: 'التقارير',
    scan: 'مسح',
    comprehensiveLogistics: 'حلول لوجستية شاملة مصممة خصيصاً لاحتياجات عملك',
    readyToStreamline: 'هل أنت مستعد لتبسيط عملية الشراء العالمية الخاصة بك؟',
    joinThousands: 'انضم إلى الآلاف من الشركات الراضية التي تثق في SourceMyGoods لتلبية احتياجاتها اللوجستية',
    
    // Customer Dashboard
    welcome: 'مرحباً',
    customerFallback: 'عميل',
    yourShipments: 'شحناتك',
    onTheWay: 'في الطريق',
    yourInvoices: 'فواتيرك',
    amountDue: 'المبلغ المستحق',
    visualBreakdownShipments: 'توزيع مرئي لشحناتك حسب الحالة',
    details: 'التفاصيل',
    
    // Contact Dialog
    getInTouch: 'تواصل معنا',
    chooseContactMethod: 'اختر طريقة الاتصال المفضلة لديك. نحن هنا للمساعدة في جميع احتياجات الشراء والشحن الخاصة بك.',
    emailUs: 'راسلنا',
    responseWithin24Hours: 'الرد خلال 24 ساعة',
    emailCopied: '✓ تم نسخ البريد الإلكتروني!',
    emailAddressCopied: 'تم نسخ عنوان البريد الإلكتروني',
    callUs: 'اتصل بنا',
    directPhoneSupport: 'دعم هاتفي مباشر',
    phoneCopied: '✓ تم نسخ رقم الهاتف!',
    phoneNumberCopied: 'تم نسخ رقم الهاتف',
    callNow: 'اتصل الآن',
    whatsappBusiness: 'واتساب للأعمال',
    chatWithTeam: 'دردش مع فريقنا فوراً',
    openWhatsApp: 'فتح واتساب',
    businessHours: 'ساعات العمل',
    businessHoursTime: 'الإثنين - الجمعة: 9:00 صباحاً - 6:00 مساءً (توقيت الصين)',
    sendQuickMessage: 'إرسال رسالة سريعة',
    yourName: 'اسمك',
    yourEmail: 'بريدك الإلكتروني',
    message: 'الرسالة',
    tellUsAboutNeeds: 'أخبرنا عن احتياجات الشراء أو الشحن الخاصة بك...',
    sendMessage: 'إرسال رسالة',
    opensEmailClient: 'سيتم فتح تطبيق البريد الإلكتروني الافتراضي',
    openingEmailClient: 'جاري فتح البريد الإلكتروني...',
    defaultEmailWillOpen: 'سيتم فتح تطبيق البريد الإلكتروني الافتراضي',

    // Dashboard (Admin/Warehouse) hardcoded strings
    dashboardTitle: 'لوحة التحكم',
    overviewOfOperations: 'نظرة عامة على عمليات الشحن الخاصة بك',
    exportPdf: 'تصدير PDF',
    exportExcel: 'تصدير Excel',
    dateRangeFilter: 'فلتر نطاق التاريخ',
    filterDashboardByDate: 'تصفية بيانات لوحة التحكم حسب نطاق التاريخ',
    startDate: 'تاريخ البداية',
    endDate: 'تاريخ النهاية',
    applyFilter: 'تطبيق الفلتر',
    clearFilter: 'مسح الفلتر',
    activeShipmentsInSystem: 'الشحنات النشطة في النظام',
    registeredCustomerAccounts: 'حسابات العملاء المسجلة',
    totalWarehouses: 'إجمالي المستودعات',
    yourWarehouseSingle: 'مستودعك',
    totalWarehousesLabel: 'إجمالي المستودعات',
    currentlyActiveTrips: 'الرحلات النشطة حالياً',
    awaitingPayment: 'في انتظار الدفع',
    totalRevenueEarned: 'إجمالي الإيرادات المكتسبة',
    revenueByWarehouseLabel: 'الإيرادات حسب المستودع:',
    revenueBreakdown: 'تفصيل الإيرادات:',
    shipmentStatusDistribution: 'توزيع حالة الشحنات',
    visualBreakdownAllShipments: 'توزيع مرئي لجميع الشحنات حسب الحالة',
    invoiceStatusOverview: 'نظرة عامة على حالة الفواتير',
    invoiceDistributionPaymentStatus: 'توزيع الفواتير الحالي وحالة الدفع',
    noRecentShipments: 'لا توجد شحنات حديثة',
    detailedViewShipmentsStatus: 'عرض تفصيلي للشحنات بهذه الحالة',
    detailedViewCustomersByStatus: 'عرض تفصيلي للعملاء حسب الحالة',
    detailedViewWarehousesByType: 'عرض تفصيلي للمستودعات حسب النوع',
    detailedViewTripsByStatus: 'عرض تفصيلي للرحلات حسب الحالة',
    detailedViewInvoicesByStatus: 'عرض تفصيلي للفواتير حسب الحالة',
    noItemsFound: 'لا توجد عناصر',
    noItemsFoundInSystem: 'لا توجد عناصر في النظام.',
    showing: 'عرض',
    processingShipments: 'الشحنات قيد المعالجة',
    shipmentsInTransit: 'الشحنات في الطريق',
    shipmentsAtDestination: 'الشحنات في الوجهة',
    deliveredShipments: 'الشحنات المُسلَّمة',
    tripsLabel: 'الرحلات',
    invoicesLabel: 'الفواتير',
    warehousesLabel: 'المستودعات',
    customersLabel: 'العملاء',
    allCustomersLabel: 'كل العملاء',
    allWarehousesLabel: 'كل المستودعات',
    allInvoicesLabel: 'كل الفواتير',
    sectionDetails: 'تفاصيل القسم',
    emailField: 'البريد الإلكتروني:',
    phoneField: 'الهاتف:',
    locationField: 'الموقع:',
    shipmentsField: 'الشحنات:',
    codeField: 'الرمز:',
    addressField: 'العنوان:',
    managerField: 'المدير:',
    capacityField: 'السعة:',
    currentField: 'الحالي:',
    units: 'وحدات',
    driverField: 'السائق:',
    vehicleField: 'المركبة:',
    originField: 'المصدر:',
    weightField: 'الوزن:',
    destinationField: 'الوجهة:',
    shipmentsFieldShort: 'الشحنات:',
    packagesField: 'الطرود:',
    createdField: 'تاريخ الإنشاء:',
    estArrival: 'الوصول المتوقع:',
    customerField: 'العميل:',
    dueField: 'تاريخ الاستحقاق:',
    tripField: 'الرحلة:',
    volumeField: 'الحجم:',
    freightField: 'الشحن:',
    estDelivery: 'التسليم المتوقع:',
    unassigned: 'غير محدد',

    // Footer
    quickLinks: 'روابط سريعة',
    ourServices: 'خدماتنا',
    termsAndConditions: 'الشروط والأحكام',
    allRightsReservedFull: '© {year} SourceMyGoods. جميع الحقوق محفوظة.',
    operatedBy: 'تشغيل بواسطة',
    footerTagline: 'حلول الشراء الاحترافي والفحص والشحن العالمي',

    // Register page
    personalInformationSection: 'المعلومات الشخصية',
    fullName: 'الاسم الكامل',
    fullNamePlaceholder: 'أدخل اسمك الكامل',
    emailPlaceholder: 'your.email@example.com',
    phoneNumber: 'رقم الهاتف',
    phoneNumberHint: 'أدخل رقم الهاتف بدون رمز الدولة',
    addressInformation: 'معلومات العنوان',
    streetAddress: 'عنوان الشارع',
    streetAddressPlaceholder: '123 الشارع الرئيسي',
    selectCountry: 'اختر دولة',
    stateProvince: 'الولاية/المحافظة',
    selectState: 'اختر ولاية',
    selectCountryFirst: 'اختر الدولة أولاً',
    noStatesAvailable: 'لا توجد ولايات متاحة',
    selectCity: 'اختر مدينة',
    selectStateFirst: 'اختر الولاية أولاً',
    noCitiesAvailable: 'لا توجد مدن متاحة',
    shippingPreferences: 'تفضيلات الشحن',
    preferredDestinationWarehouse: 'المستودع المفضل للوجهة',
    selectWarehouse: 'اختر مستودعاً',
    noWarehousesAvailable: 'لا توجد مستودعات متاحة',
    defaultWarehouseHint: 'اختر المستودع الذي تريد تسليم شحناتك إليه افتراضياً',
    accountInformationSection: 'معلومات الحساب',
    chooseUsername: 'اختر اسم مستخدم',
    createPassword: 'أنشئ كلمة مرور',
    confirmPasswordPlaceholder: 'تأكيد كلمة المرور',
    iAcceptThe: 'أوافق على',
    andAcknowledge: 'وأقر بأن معلوماتي ستُستخدم وفقاً لسياسة خصوصية SourceMyGoods.',
    continueToVerification: 'المتابعة إلى التحقق',
    sendingCode: 'جاري الإرسال...',
    verifyAndCreateAccount: 'تحقق وأنشئ الحساب',
    creatingAccount: 'جاري إنشاء الحساب...',
    checkEmailInbox: 'تحقق من صندوق البريد الوارد',
    sentSixDigitCode: 'لقد أرسلنا رمز تحقق مكوناً من 6 أرقام إلى',
    checkSpamFolder: 'تحقق من مجلد الرسائل غير المرغوب فيها (Spam)',
    spamFolderHint: 'إذا لم تجد البريد في صندوق الوارد. ينتهي صلاحية الرمز خلال 10 دقائق.',
    verificationCodeLabel: 'رمز التحقق',
    enterSixDigitCode: 'أدخل الرمز المكون من 6 أرقام',
    codeSentTo: 'تم إرسال الرمز إلى',
    back: '→ رجوع',
    resendCode: 'إعادة إرسال الرمز',
    sending: 'جاري الإرسال...',
    mustAcceptTerms: 'يجب قبول الشروط والأحكام للتسجيل',
    passwordsDoNotMatchError: 'كلمات المرور غير متطابقة',
    passwordTooShort: 'يجب أن تكون كلمة المرور 6 أحرف على الأقل',
    emailAlreadyRegistered: 'هذا البريد الإلكتروني مسجل بالفعل. يرجى استخدام بريد آخر أو تسجيل الدخول.',
    verificationCodeSent: 'تم إرسال رمز التحقق!',
    sixDigitCodeSentTo: 'تم إرسال رمز مكون من 6 أرقام إلى',
    invalidVerificationCode: 'رمز التحقق غير صحيح. يرجى التحقق والمحاولة مرة أخرى.',
    verificationCodeExpired: 'انتهت صلاحية رمز التحقق. يرجى طلب رمز جديد.',
    accountCreated: 'تم إنشاء الحساب!',
    accountCreatedDesc: 'مرحباً! رمز عميلك هو: {code}. يمكنك الآن تسجيل الدخول.',
    registrationFailed: 'فشل التسجيل. يرجى المحاولة مرة أخرى.',

    // Sourcing Requests
    sourcingRequests: 'طلبات التوريد',
    requestProductsAndReceiveQuotes: 'اطلب المنتجات واستلم عروض الأسعار',
    newRequest: 'طلب جديد',
    createSourcingRequest: 'إنشاء طلب توريد',
    tellUsWhatYouNeed: 'أخبرنا بما تحتاج وسنجد لك أفضل الموردين',
    totalRequests: 'إجمالي الطلبات',
    allTimeRequests: 'جميع الطلبات',
    pending: 'قيد الانتظار',
    awaitingQuote: 'في انتظار عرض السعر',
    quoted: 'تم تقديم السعر',
    awaitingApproval: 'في انتظار الموافقة',
    sourcingRevenue: 'إيرادات التوريد',
    thisMonth: 'هذا الشهر',
    searchByRequestNumber: 'البحث برقم الطلب، اسم المنتج...',
    searchByRequestNumberCustomer: 'البحث برقم الطلب، اسم المنتج، العميل...',
    filterByCustomer: 'التصفية حسب العميل',
    allCustomers: 'جميع العملاء',
    request: 'طلب',
    requests: 'طلبات',
    loadingRequests: 'جاري تحميل الطلبات...',
    noSourcingRequestsFound: 'لم يتم العثور على طلبات توريد',
    createYourFirstRequest: 'أنشئ طلبك الأول',
    items: 'العناصر',
    item: 'عنصر',
    quantity: 'الكمية',
    unit: 'الوحدة',
    referenceLink: 'رابط مرجعي',
    referenceLinks: 'روابط مرجعية',
    pricePerUnit: 'السعر لكل وحدة',
    grandTotal: 'الإجمالي الكلي',
    view: 'عرض',
    approve: 'موافقة',
    quote: 'تسعير',
    updateStatus: 'تحديث الحالة',
    deleteRequest: 'حذف الطلب',
    printDownload: 'طباعة/تحميل',
    underReview: 'قيد المراجعة',
    waitingForPayment: 'في انتظار الدفع',
    paymentReceived: 'تم استلام الدفع',
    completed: 'مكتمل',
    cancelled: 'ملغى',
    
    // Multi-Item Sourcing Form
    productName: 'اسم المنتج',
    productNamePlaceholder: 'مثال: سماعات لاسلكية',
    descriptionLabel: 'الوصف',
    descriptionPlaceholder: 'وصف تفصيلي، المواصفات، المتطلبات...',
    targetPriceOptional: 'السعر المستهدف (اختياري)',
    targetPricePlaceholder: 'الميزانية المتوقعة لكل وحدة',
    productPhotos: 'صور المنتج',
    uploadOrDragPhotos: 'ارفع أو اسحب الصور هنا',
    uploadPhotos: 'رفع الصور',
    clickOrDrag: 'انقر للرفع أو اسحب وأفلت',
    maxFileSize: 'الحد الأقصى لحجم الملف: 10 ميجابايت',
    addReferenceLinks: 'إضافة روابط مرجعية',
    addFromPlatforms: 'أضف روابط المنتجات من المنصات الشهيرة',
    pasteProductLink: 'الصق رابط المنتج (مثلاً من أمازون، علي بابا...)',
    selectPlatform: 'اختر المنصة',
    addLink: 'إضافة رابط',
    removeItem: 'إزالة العنصر',
    addAnotherItem: 'إضافة عنصر آخر',
    generalRequestInfo: 'معلومات الطلب العامة',
    appliestoEntireOrder: 'تنطبق على الطلب بأكمله',
    specialRequirementsOptional: 'متطلبات خاصة (اختياري)',
    packagingCertifications: 'التغليف، الشهادات، تفضيلات الشحن...',
    expectedDeliveryDate: 'تاريخ التسليم المتوقع',
    selectDate: 'اختر التاريخ',
    submitSourcingRequest: 'إرسال طلب التوريد',
    submittingRequest: 'جاري إرسال الطلب...',
    atLeastOneItemRequired: 'مطلوب عنصر واحد على الأقل',
    pleaseCompleteAllFields: 'يرجى إكمال جميع الحقول المطلوبة لجميع العناصر',

    // Terms & Conditions page
    termsTitle: 'الشروط والأحكام',
    lastUpdated: 'آخر تحديث:',
    agreementToTerms: 'الموافقة على الشروط',
    termsSection1Title: '1. مقدمة',
    termsSection1P1: 'مرحباً بك في SourceMyGoods ("الشركة"، "نحن"، "لنا"). تحكم هذه الشروط والأحكام ("الشروط") استخدامك لمنصة وخدمات إدارة الشحن واللوجستيات لدينا.',
    termsSection1P2: 'بالوصول إلى SourceMyGoods أو استخدامه، فإنك توافق على الالتزام بهذه الشروط. إذا كنت لا توافق على أي جزء من هذه الشروط، فلا يجوز لك استخدام الخدمة.',
    termsSection2Title: '2. استخدام الخدمة',
    termsSection2Intro: 'توفر منصتنا خدمات التوريد والفحص والشحن وإدارة اللوجستيات. توافق على:',
    termsSection2Li1: 'تقديم معلومات تسجيل دقيقة وكاملة',
    termsSection2Li2: 'الحفاظ على أمان بيانات اعتماد حسابك',
    termsSection2Li3: 'استخدام الخدمة لأغراض مشروعة فقط',
    termsSection2Li4: 'عدم التدخل في الخدمة أو تعطيلها',
    termsSection2Li5: 'الامتثال لجميع القوانين واللوائح المعمول بها',
    termsSection3Title: '3. تسجيل الحساب',
    termsSection3Intro: 'عند إنشاء حساب معنا، يجب عليك:',
    termsSection3Li1: 'أن يكون عمرك 18 عاماً على الأقل',
    termsSection3Li2: 'تقديم معلومات صادقة ودقيقة',
    termsSection3Li3: 'الحفاظ على معلومات حسابك وتحديثها فوراً',
    termsSection3Li4: 'الحفاظ على سرية كلمة المرور',
    termsSection3Li5: 'تحمل المسؤولية عن جميع الأنشطة تحت حسابك',
    termsSection4Title: '4. الخدمات والرسوم',
    termsSection4Intro: 'تقدم SourceMyGoods الخدمات التالية:',
    termsSection4Li1: 'توريد المنتجات العالمي والتحقق من الموردين',
    termsSection4Li2: 'خدمات الفحص وضمان الجودة',
    termsSection4Li3: 'الشحن الجوي والبحري',
    termsSection4Li4: 'المساعدة في التخليص الجمركي',
    termsSection4Li5: 'إدارة المستودعات والمخزون',
    termsSection4Li6: 'تتبع الشحنات وإدارتها',
    termsSection4P2: 'سيتم إبلاغك بوضوح برسوم الخدمات قبل أي معاملة. توافق على دفع جميع الرسوم والتكاليف المطبقة على حسابك.',
    termsSection5Title: '5. الشحن والتسليم',
    termsSection5Intro: 'نسعى لتقديم تقديرات تسليم دقيقة، ولكن:',
    termsSection5Li1: 'أوقات التسليم هي تقديرات وغير مضمونة',
    termsSection5Li2: 'قد تخضع الشحنات الدولية لتأخيرات جمركية',
    termsSection5Li3: 'أنت مسؤول عن تقديم معلومات التسليم الدقيقة',
    termsSection5Li4: 'ينتقل خطر الفقدان إليك عند التسليم للناقل',
    termsSection6Title: '6. المسؤولية والتأمين',
    termsSection6P1: 'نوصي بشراء تأمين شحن للمواد القيّمة. تقتصر مسؤوليتنا عن البضائع المفقودة أو التالفة على القيمة المعلنة أو حدود الناقل المعمول بها، أيهما أقل.',
    termsSection7Title: '7. البنود المحظورة',
    termsSection7Intro: 'لا يجوز لك شحن:',
    termsSection7Li1: 'المواد غير القانونية أو الخاضعة للتنظيم',
    termsSection7Li2: 'المواد الخطرة بدون وثائق مناسبة',
    termsSection7Li3: 'البنود المحظورة بموجب سياسات الناقل',
    termsSection7Li4: 'البضائع المزيفة أو المنتهكة للحقوق',
    termsSection7Li5: 'البنود المقيدة بقوانين دولة الوجهة',
    termsSection8Title: '8. الخصوصية وحماية البيانات',
    termsSection8P1: 'نجمع ونعالج معلوماتك الشخصية وفقاً لسياسة الخصوصية الخاصة بنا. باستخدام خدماتنا، فإنك توافق على هذا الجمع والمعالجة.',
    termsSection9Title: '9. الملكية الفكرية',
    termsSection9P1: 'منصة SourceMyGoods، بما في ذلك جميع المحتوى والميزات والوظائف، مملوكة لنا ومحمية بموجب حقوق النشر والعلامات التجارية وقوانين الملكية الفكرية الأخرى.',
    termsSection10Title: '10. الإنهاء',
    termsSection10P1: 'نحتفظ بالحق في إنهاء حسابك أو تعليقه فوراً، دون إشعار مسبق، في حالة انتهاك هذه الشروط أو لأي سبب آخر وفق تقديرنا المطلق.',
    termsSection11Title: '11. تحديد المسؤولية',
    termsSection11P1: 'إلى أقصى حد يسمح به القانون، لن تكون SourceMyGoods مسؤولة عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية أو عقابية ناتجة عن استخدامك للخدمة.',
    termsSection12Title: '12. التغييرات على الشروط',
    termsSection12P1: 'نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سنخطرك بأي تغييرات عن طريق نشر الشروط الجديدة على هذه الصفحة وتحديث تاريخ "آخر تحديث".',
    termsSection13Title: '13. معلومات الاتصال',
    termsSection13Intro: 'إذا كانت لديك أي أسئلة حول هذه الشروط، يرجى الاتصال بنا على:',
    termsFooterNote: 'بإنشاء حساب واستخدام SourceMyGoods، فإنك تقر بأنك قد قرأت وفهمت ووافقت على الالتزام بهذه الشروط والأحكام.',
    backToRegistration: '→ العودة إلى التسجيل',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'ar' || saved === 'en') ? saved : 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key: string, params?: Record<string, any>): string => {
    let text = translations[language][key as keyof typeof translations.en] || key;

    // Replace template parameters like {customerName}
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        text = text.replace(new RegExp(`{${param}}`, 'g'), String(value));
      });
    }

    return text;
  };

  const dir = language === 'ar' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ language, setLanguage: setLanguageState, t, dir }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}
